import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, MicrosoftExcelLogo, WhatsappLogo, EnvelopeSimple, ChatCircle,
  Trash, PencilSimple, ShieldCheck, Warning, Printer, Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/reportExport";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import { openPrintStatement } from "@/lib/printStatement";
import { DscDocumentCell, validatePdfFiles, uploadDscPdfs } from "@/components/DscDocumentCell";

const today = () => new Date().toISOString().slice(0, 10);
const plusYears = (isoDate, years) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
};

const emptyLine = () => ({
  item_type: "other",
  item_id: "",
  item_name: "",
  quantity: 1,
  amount: 0,
  tax_rate: 18,
  description: "",
});

const DSC_CLASSES = [
  "Class 3 · Signing",
  "Class 3 · Encryption",
  "Class 3 · Signing + Encryption (Combo)",
  "Class 3 · Organisation (Signing)",
  "Class 3 · Organisation (Encryption)",
  "DGFT",
  "Document Signer Certificate",
];

const CERT_COMPANIES = [
  "eMudhra",
  "Capricorn",
  "VSign",
  "SafeScrypt",
  "NSDL e-Gov",
  "Pantasign",
  "IDsign",
  "XtraTrust",
  "ProDigiSign",
  "Other",
];

const PAYMENTS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
  { value: "rtgs_neft", label: "RTGS / NEFT" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

function emptyForm() {
  const d = today();
  return {
    date: d,
    customer_id: "",
    customer_name: "",
    mobile: "",
    email: "",
    address: "",
    pincode: "",
    pan_number: "",
    aadhar_number: "",
    item_type: "",
    item_id: "",
    item_name: "",
    class_type: "Class 3 · Signing",
    certifying_company: "eMudhra",
    kyc_id: "",
    order_id: "",
    description: "",
    payment_type: "cash",
    payment_other: "",
    payment_reference: "",
    amount: 0,
    tax_rate: 18,
    lines: [emptyLine()],
    validity_from: d,
    validity_to: plusYears(d, 2),
    notes: "",
    auto_create_customer: true,   // Default ON — most operators want auto-create
  };
}

function DscForm({ customers, products, services, editing, onClose, onSaved, certCompaniesPresets, certCompaniesUsed }) {
  const { dateFloor } = useAuth();
  const [docFiles, setDocFiles] = useState([]);
  const existingDocs = editing?.documents || [];
  const [f, setF] = useState(() => {
    if (!editing) return emptyForm();
    // Legacy record with no lines[] → synth one from top-level single-item fields
    let lines = editing.lines || [];
    if (!lines.length) {
      lines = [{
        item_type: editing.item_type || "other",
        item_id: editing.item_id || "",
        item_name: editing.item_name || "",
        quantity: 1,
        amount: editing.amount || 0,
        tax_rate: editing.tax_rate ?? 18,
        description: "",
      }];
    }
    return { ...emptyForm(), ...editing, lines };
  });
  const [saving, setSaving] = useState(false);
  // If the record has a class that isn't in our preset list (older records / user-added),
  // start in "custom" mode so the value is preserved.
  const [classMode, setClassMode] = useState(
    editing?.class_type && !DSC_CLASSES.includes(editing.class_type) ? "custom" : "preset"
  );
  // Combined list = built-in presets + any custom values already used by this tenant
  const authorityOptions = useMemo(() => {
    const built = certCompaniesPresets && certCompaniesPresets.length ? certCompaniesPresets : CERT_COMPANIES;
    const used = certCompaniesUsed || [];
    return Array.from(new Set([...built.filter((x) => x !== "Other"), ...used, "Other"]));
  }, [certCompaniesPresets, certCompaniesUsed]);
  const [certMode, setCertMode] = useState(
    editing?.certifying_company && !authorityOptions.includes(editing.certifying_company) ? "custom" : "preset"
  );

  // Auto-recompute validity_to when validity_from changes (only for new records)
  const setValidityFrom = (v) => setF({ ...f, validity_from: v, validity_to: plusYears(v, 2) });

  // ── Multi-line item helpers ───────────────────────────────────────────
  const setLine = (i, patch) => setF((prev) => ({
    ...prev,
    lines: prev.lines.map((ln, idx) => idx === i ? { ...ln, ...patch } : ln),
  }));
  const addLine = () => setF((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const rmLine = (i) => setF((prev) => ({
    ...prev,
    lines: prev.lines.length > 1 ? prev.lines.filter((_, idx) => idx !== i) : prev.lines,
  }));
  const pickLineItem = (i, v) => {
    if (!v || v === "none") { setLine(i, { item_type: "other", item_id: "", item_name: "" }); return; }
    const [type, id] = v.split(":");
    const list = type === "product" ? products : services;
    const it = (list || []).find((x) => x.id === id);
    const rate = type === "product" ? (it?.sale_price ?? 0) : (it?.price ?? 0);
    const tax = it?.tax_rate;
    setLine(i, {
      item_type: type,
      item_id: id,
      item_name: it?.name || "",
      amount: Number(f.lines[i].amount) > 0 ? f.lines[i].amount : (rate || 0),
      tax_rate: (tax !== undefined && tax !== null) ? tax : (f.lines[i].tax_rate ?? 18),
    });
  };
  const linesGrandTotal = useMemo(
    () => (f.lines || []).reduce(
      (s, ln) => s + (Number(ln.amount) || 0) * Math.max(1, Number(ln.quantity) || 1),
      0,
    ),
    [f.lines],
  );

  const pickCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setF({
      ...f,
      customer_id: id,
      customer_name: c?.name || "",
      mobile: c?.phone || f.mobile,
      email: c?.email || f.email,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!f.mobile.trim()) { toast.error("Mobile number is required"); return; }
    if (!f.validity_from || !f.validity_to) { toast.error("Validity dates are required"); return; }
    if (!existingDocs.length && !docFiles.length) { toast.error("Please attach the DSC document (PDF) — it is mandatory"); return; }
    const pdfErr = await validatePdfFiles(docFiles);
    if (pdfErr) { toast.error(pdfErr); return; }
    // Sanitize lines: drop empty rows, always keep at least one so backend mirroring works
    const cleanLines = (f.lines || []).map((ln) => ({
      item_type: ln.item_type || "other",
      item_id: ln.item_id || "",
      item_name: (ln.item_name || "").trim(),
      quantity: Math.max(1, parseInt(ln.quantity, 10) || 1),
      amount: Number(ln.amount) || 0,
      tax_rate: Number(ln.tax_rate) || 0,
      description: (ln.description || "").trim(),
    })).filter((ln) => ln.item_name || ln.amount > 0 || ln.item_id);
    const payload = { ...f, lines: cleanLines.length ? cleanLines : [emptyLine()] };
    setSaving(true);
    try {
      let dscId = editing?.id;
      if (editing?.id) {
        await api.patch(`/dsc/${editing.id}`, payload);
      } else {
        const { data } = await api.post("/dsc", payload);
        dscId = data.id;
      }
      if (docFiles.length) {
        try {
          await uploadDscPdfs(dscId, docFiles);
        } catch (upErr) {
          toast.error(`Entry saved but PDF upload failed: ${formatApiError(upErr)}. Upload it from the Document column.`);
          onSaved(); onClose(); return;
        }
      }
      toast.success(editing?.id ? "DSC updated" : "DSC record added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Purchase Date *</Label>
          <Input type="date" required value={f.date} min={dateFloor}
            onChange={(e) => setF({ ...f, date: e.target.value })}
            className="font-mono" data-testid="dsc-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">DSC Class</Label>
          {classMode === "preset" ? (
            <Select value={f.class_type} onValueChange={(v) => {
              if (v === "__custom__") { setClassMode("custom"); setF({ ...f, class_type: "" }); return; }
              setF({ ...f, class_type: v });
            }}>
              <SelectTrigger data-testid="dsc-class"><SelectValue placeholder="Choose class" /></SelectTrigger>
              <SelectContent>
                {DSC_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                <SelectItem value="__custom__" data-testid="dsc-class-custom">+ Add custom class…</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-2">
              <Input value={f.class_type} autoFocus
                onChange={(e) => setF({ ...f, class_type: e.target.value })}
                placeholder="Enter custom DSC class" data-testid="dsc-class-input" />
              <Button type="button" variant="outline" className="h-9 px-2 text-[11px]"
                onClick={() => { setClassMode("preset"); setF({ ...f, class_type: DSC_CLASSES[0] }); }}>
                ← Preset
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Existing Customer</Label>
          <SearchableCustomerSelect
            value={f.customer_id}
            onChange={(id) => id === "__clear__" ? setF({ ...f, customer_id: "" }) : pickCustomer(id)}
            customers={customers}
            createValue="__clear__"
            createLabel="None / walk-in customer"
            placeholder="Search & pick customer"
            testid="dsc-customer"
            getSubtitle={(c) => c.phone}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Customer Name *</Label>
          <Input required value={f.customer_name}
            onChange={(e) => setF({ ...f, customer_name: e.target.value })}
            data-testid="dsc-cust-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Mobile Number *</Label>
          <Input required inputMode="tel" value={f.mobile}
            onChange={(e) => setF({ ...f, mobile: e.target.value })}
            placeholder="10-digit mobile" data-testid="dsc-mobile" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Email ID</Label>
        <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
          data-testid="dsc-email" />
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Address</Label>
        <Input value={f.address}
          onChange={(e) => setF({ ...f, address: e.target.value })}
          placeholder="Door No, Street, Area, City, State"
          data-testid="dsc-address" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Pincode</Label>
          <Input inputMode="numeric" maxLength={6} value={f.pincode}
            onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/[^0-9]/g, "").slice(0, 6) })}
            placeholder="6-digit PIN"
            className="font-mono"
            data-testid="dsc-pincode" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">PAN Number</Label>
          <Input maxLength={10} value={f.pan_number}
            onChange={(e) => setF({ ...f, pan_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) })}
            placeholder="AAAAA9999A"
            className="font-mono uppercase"
            data-testid="dsc-pan" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Aadhaar Number</Label>
          <Input inputMode="numeric" maxLength={12} value={f.aadhar_number}
            onChange={(e) => setF({ ...f, aadhar_number: e.target.value.replace(/[^0-9]/g, "").slice(0, 12) })}
            placeholder="12-digit Aadhaar"
            className="font-mono"
            data-testid="dsc-aadhar" />
        </div>
      </div>

      {/* New-customer auto-create toggle — only meaningful when no existing customer picked */}
      {!f.customer_id && (
        <div className="flex items-center gap-2 rounded-md border border-border/70 bg-muted/30 px-3 py-2">
          <input
            id="dsc-auto-create-cust"
            type="checkbox"
            checked={!!f.auto_create_customer}
            onChange={(e) => setF({ ...f, auto_create_customer: e.target.checked })}
            className="h-4 w-4 accent-primary"
            data-testid="dsc-auto-create-cust"
          />
          <label htmlFor="dsc-auto-create-cust" className="text-[12.5px] cursor-pointer">
            Also add this person to the <b>Customers</b> master so their data auto-fills everywhere (invoices, quotations, ledger…).
          </label>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Certifying Authority *</Label>
          {certMode === "preset" ? (
            <Select value={f.certifying_company} onValueChange={(v) => {
              if (v === "__custom__") { setCertMode("custom"); setF({ ...f, certifying_company: "" }); return; }
              setF({ ...f, certifying_company: v });
            }}>
              <SelectTrigger data-testid="dsc-cert-company"><SelectValue placeholder="Which company issued it?" /></SelectTrigger>
              <SelectContent>
                {authorityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                <SelectItem value="__custom__" data-testid="dsc-cert-company-custom">+ Add custom authority…</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex gap-2">
              <Input value={f.certifying_company} autoFocus
                onChange={(e) => setF({ ...f, certifying_company: e.target.value })}
                placeholder="Type CA name (e.g. Fintech CA)"
                data-testid="dsc-cert-company-input" />
              <Button type="button" variant="outline" className="h-9 px-2 text-[11px]"
                onClick={() => { setCertMode("preset"); setF({ ...f, certifying_company: authorityOptions[0] || "eMudhra" }); }}>
                ← Preset
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">KYC ID</Label>
          <Input value={f.kyc_id}
            onChange={(e) => setF({ ...f, kyc_id: e.target.value })}
            placeholder="e.g. KYC-2026-8829"
            className="font-mono" data-testid="dsc-kyc-id" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Order ID</Label>
          <Input value={f.order_id}
            onChange={(e) => setF({ ...f, order_id: e.target.value })}
            placeholder="e.g. eM-98341227"
            className="font-mono" data-testid="dsc-order-id" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Description <span className="text-muted-foreground">(shown on list &amp; reports)</span></Label>
        <Input value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })}
          placeholder="e.g. Renewal for GST filing — Q1"
          data-testid="dsc-description" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Payment Type *</Label>
          <Select value={f.payment_type} onValueChange={(v) => setF({ ...f, payment_type: v })}>
            <SelectTrigger data-testid="dsc-payment"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {f.payment_type === "other" && (
          <div className="space-y-1.5">
            <Label className="eyebrow">Specify Other *</Label>
            <Input required value={f.payment_other}
              onChange={(e) => setF({ ...f, payment_other: e.target.value })}
              data-testid="dsc-payment-other" />
          </div>
        )}
        <div className={`space-y-1.5 ${f.payment_type === "other" ? "" : "md:col-span-2"}`}>
          <Label className="eyebrow">Payment Reference</Label>
          <Input value={f.payment_reference}
            onChange={(e) => setF({ ...f, payment_reference: e.target.value })}
            placeholder="Cheque #, UPI ref, txn id…" />
        </div>
      </div>

      {/* ── Line Items table (multi-item billing) ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <Label className="eyebrow">Line Items</Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Add one row per product / service billed on this DSC. On <span className="font-semibold text-foreground">Send to Temp Inv</span>, a single temp invoice <span className="font-mono">VT/TEMP/nnnn</span> is generated with all lines. Rate + tax auto-fill from master (still editable).
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={addLine} data-testid="dsc-add-line">
            <Plus size={12} className="mr-1" /> Add line
          </Button>
        </div>
        <div className="rounded border border-border overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-2.5 py-2 font-semibold w-[36%]">Product / Service / Other</th>
                <th className="text-right px-2 py-2 font-semibold w-[10%]">Qty</th>
                <th className="text-right px-2 py-2 font-semibold w-[18%]">Rate (₹)</th>
                <th className="text-right px-2 py-2 font-semibold w-[12%]">Tax %</th>
                <th className="text-right px-2 py-2 font-semibold w-[18%]">Line Total</th>
                <th className="px-1 py-2 w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {(f.lines || []).map((ln, i) => {
                const lineTotal = (Number(ln.amount) || 0) * Math.max(1, Number(ln.quantity) || 1);
                return (
                  <tr key={i} className="border-t border-border align-top">
                    <td className="px-2.5 py-2 space-y-1.5">
                      <Select value={ln.item_id ? `${ln.item_type}:${ln.item_id}` : "none"} onValueChange={(v) => pickLineItem(i, v)}>
                        <SelectTrigger className="h-8" data-testid={`dsc-line-${i}-item`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Other / DSC (use class + authority)</SelectItem>
                          {(products || []).map((p) => (
                            <SelectItem key={`p-${p.id}`} value={`product:${p.id}`}>Product · {p.name}</SelectItem>
                          ))}
                          {(services || []).map((s) => (
                            <SelectItem key={`s-${s.id}`} value={`service:${s.id}`}>Service · {s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!ln.item_id && (
                        <Input
                          value={ln.item_name}
                          onChange={(e) => setLine(i, { item_name: e.target.value })}
                          placeholder="Item name / short label (blank = DSC · class · authority)"
                          className="h-8"
                          data-testid={`dsc-line-${i}-name`}
                        />
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={1} step={1} value={ln.quantity}
                        onChange={(e) => setLine(i, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                        className="font-mono h-8 text-right min-w-[70px] tabular-nums" data-testid={`dsc-line-${i}-qty`} />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={0} step="0.01" value={ln.amount}
                        onChange={(e) => setLine(i, { amount: Number(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="font-mono h-8 text-right min-w-[100px] tabular-nums" data-testid={`dsc-line-${i}-rate`} />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" min={0} max={28} step="0.01" value={ln.tax_rate}
                        onChange={(e) => setLine(i, { tax_rate: Number(e.target.value) || 0 })}
                        className="font-mono h-8 text-right min-w-[70px] tabular-nums" data-testid={`dsc-line-${i}-tax`} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{inr(lineTotal)}</td>
                    <td className="px-1 py-2 text-right">
                      {f.lines.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                          onClick={() => rmLine(i)} data-testid={`dsc-line-${i}-remove`}>
                          <Trash size={13} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-muted/30">
                <td colSpan={4} className="px-2.5 py-2 text-right eyebrow">DSC Sale Price (billable)</td>
                <td className="px-2 py-2 text-right font-mono font-semibold" data-testid="dsc-lines-total">{inr(linesGrandTotal)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Grand total must be &gt; 0 to enable "Send to Temp Invoice" after saving.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="eyebrow">Validity From *</Label>
          <Input type="date" required value={f.validity_from} min={dateFloor}
            onChange={(e) => setValidityFrom(e.target.value)}
            className="font-mono" data-testid="dsc-validity-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Validity To (auto = +2 years) *</Label>
          <Input type="date" required value={f.validity_to}
            onChange={(e) => setF({ ...f, validity_to: e.target.value })}
            className="font-mono" data-testid="dsc-validity-to" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">
          DSC Documents (PDF only, max 5 MB each, select one or more) {existingDocs.length ? "" : "*"}
        </Label>
        <Input type="file" multiple accept="application/pdf,.pdf"
          onChange={(e) => setDocFiles(Array.from(e.target.files || []))}
          data-testid="dsc-form-doc-input" />
        {docFiles.length > 0 && (
          <p className="text-[11px] text-muted-foreground" data-testid="dsc-form-doc-selected">
            {docFiles.length} file(s) selected: {docFiles.map((x) => x.name).join(", ")}
          </p>
        )}
        {existingDocs.length > 0 && (
          <p className="text-[11px] text-muted-foreground" data-testid="dsc-form-doc-current">
            Already attached: {existingDocs.map((d) => d.name).join(", ")} — new files are added to the list.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="eyebrow">Notes</Label>
        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="dsc-save-btn">
          {saving ? "Saving…" : editing?.id ? "Update DSC" : "Save DSC"}
        </Button>
      </div>
    </form>
  );
}

const REMIND_SMS = (r, daysLeft, waNumber = "") =>
  `Hi ${r.customer_name}, your Digital Signature Certificate expires on ${r.validity_to} — ${daysLeft} day(s) left. Please renew to avoid disruption.${waNumber ? `\n\nReply / WhatsApp us: ${waNumber}` : ""}\n— ${"Vetrich Technologies"}`;

const REMIND_EMAIL_SUBJECT = (r, d) =>
  `Your DSC expires on ${r.validity_to} (${d} day(s) left) — please renew`;

const REMIND_EMAIL_BODY = (r, d, waNumber = "") =>
`Hello ${r.customer_name},

This is a friendly reminder that your Digital Signature Certificate (${r.class_type || "Class 3"}) is set to expire on ${r.validity_to}, which is ${d} day(s) away.

To avoid any disruption in filings or e-signing, please arrange for renewal at your earliest convenience. We're happy to help — just reply to this email${waNumber ? ` or WhatsApp us at ${waNumber}` : ""}.

Warm regards,
Vetrich Technologies${waNumber ? `\nWhatsApp: ${waNumber}` : ""}`;

export default function DscRegister() {
  const { activeCompanyId, activeCompany, can, activeStoreId, dateFloor } = useAuth();
  const waNumber = (activeCompany?.whatsapp_number || "").trim();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [certCompaniesPresets, setCertCompaniesPresets] = useState([]);
  const [certCompaniesUsed, setCertCompaniesUsed] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [certCompany, setCertCompany] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!activeCompanyId) return;
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => setServices([]));
    api.get("/dsc/certifying-companies").then((r) => {
      setCertCompaniesPresets(r.data.presets || []);
      setCertCompaniesUsed(r.data.used_custom || []);
    }).catch(() => {});
  }, [activeCompanyId, activeStoreId]);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({
        status: tab, q,
        ...(certCompany ? { certifying_company: certCompany } : {}),
        ...(start ? { start } : {}), ...(end ? { end } : {}),
      });
      const { data } = await api.get(`/dsc?${p}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, tab, q, certCompany, start, end]);

  const buckets = useMemo(() => {
    const b = { active: 0, expiring: 0, expired: 0 };
    rows.forEach((r) => { if (b[r._bucket] != null) b[r._bucket]++; });
    return b;
  }, [rows]);

  const del = async (id) => {
    if (!confirm("Delete this DSC record?")) return;
    try { await api.delete(`/dsc/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportExcel = () => {
    downloadCSV(`dsc-register-${new Date().toISOString().slice(0,10)}`, [
      { key: "date", label: "Purchase Date" },
      { key: "customer_name", label: "Customer" },
      { key: "mobile", label: "Mobile" },
      { key: "email", label: "Email" },
      { key: "class_type", label: "Class" },
      { key: "certifying_company", label: "Certifying Authority" },
      { key: "kyc_id", label: "KYC ID" },
      { key: "order_id", label: "Order ID" },
      { key: "description", label: "Description" },
      { key: "payment_type", label: "Payment" },
      { key: "payment_reference", label: "Payment Ref" },
      { key: "validity_from", label: "Validity From" },
      { key: "validity_to", label: "Validity To" },
      { key: "days_to_expiry", label: "Days to Expiry" },
      { key: "_bucket", label: "Status" },
    ], rows);
  };

  const printStatement = () => {
    if (!rows.length) { toast.error("No DSC records in the current view."); return; }
    const groups = { active: [], expiring: [], expired: [] };
    rows.forEach((r) => { if (groups[r._bucket]) groups[r._bucket].push(r); });
    const label = {
      active: "Active DSCs",
      expiring: "Expiring Within 30 Days",
      expired: "Expired DSCs",
    };
    const tone = { active: "", expiring: "", expired: "neg" };
    const cols = [
      { key: "date", label: "Purchased", mono: true },
      { key: "customer", label: "Customer" },
      { key: "mobile", label: "Mobile", mono: true },
      { key: "authority", label: "Authority" },
      { key: "kyc", label: "KYC / Order", mono: true },
      { key: "class_type", label: "Class" },
      { key: "description", label: "Description" },
      { key: "validity_to", label: "Expires On", mono: true },
      { key: "days_left", label: "Days Left", align: "right", mono: true },
    ];
    const sections = Object.keys(groups).filter((k) => groups[k].length).map((k) => ({
      heading: label[k],
      subheading: `${groups[k].length} certificate(s)`,
      columns: cols,
      rows: groups[k].map((r) => ({
        date: r.date || "—",
        customer: r.customer_name || "—",
        mobile: r.mobile || "—",
        authority: r.certifying_company || "—",
        kyc: [r.kyc_id, r.order_id].filter(Boolean).join(" / ") || "—",
        class_type: r.class_type || "—",
        description: r.description || "—",
        validity_to: r.validity_to || "—",
        days_left: String(r.days_to_expiry ?? "—"),
      })),
    }));
    // apply tone class on days_left column per section
    sections.forEach((s, i) => {
      const key = Object.keys(groups).filter((k) => groups[k].length)[i];
      const t = tone[key];
      if (t) s.columns = s.columns.map((c) => c.key === "days_left" ? { ...c, tone: t } : c);
    });
    const range = start || end ? `${start || "…"} → ${end || "…"}` : "All dates";
    const filterTab = tab !== "all" ? ` · Filter: ${tab}` : "";
    const res = openPrintStatement({
      title: `DSC Purchased — Customer Register (${range}${filterTab})`,
      company: activeCompany || {},
      sections,
      totals: [
        { label: "Total DSCs", value: String(rows.length) },
        { label: "Active", value: String(buckets.active), tone: "pos" },
        { label: "Expiring ≤ 30 days", value: String(buckets.expiring) },
        { label: "Expired", value: String(buckets.expired), tone: "neg" },
      ],
      note: "Contact the customer at least 15 days before validity to arrange renewal.",
    });
    if (!res.ok) toast.error(res.error);
    else toast.success(`Preparing statement for ${rows.length} DSC record(s)…`);
  };

  const sendToTemp = async (r) => {
    if (r.temp_invoice_id) {
      toast.info(`Already sent as Temp Invoice ${r.temp_invoice_number || ""}`.trim());
      return;
    }
    if (!r.customer_id) { toast.error("Link this DSC to a customer first (edit → pick from list)."); return; }
    if (!(Number(r.amount) > 0)) { toast.error("Set DSC Sale Price > 0 (edit the DSC)."); return; }
    if (!confirm(`Create a Temp Invoice for ₹${Number(r.amount).toFixed(2)} + ${r.tax_rate ?? 18}% GST for ${r.customer_name}?`)) return;
    try {
      const { data } = await api.post(`/dsc/${r.id}/send-to-temp`);
      toast.success(`Temp Invoice ${data.temp_invoice?.number || data.number} created`, { duration: 6000 });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendWhatsApp = (r) => {
    const d = r.days_to_expiry ?? 0;
    if (!r.mobile) { toast.error("No mobile number"); return; }
    const phone = r.mobile.replace(/[^\d+]/g, "").replace(/^\+/, "");
    const num = phone.length === 10 ? `91${phone}` : phone;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(REMIND_SMS(r, d, waNumber))}`, "_blank");
  };
  const sendEmail = (r) => {
    if (!r.email) { toast.error("No email on file"); return; }
    const d = r.days_to_expiry ?? 0;
    const subject = encodeURIComponent(REMIND_EMAIL_SUBJECT(r, d));
    const body = encodeURIComponent(REMIND_EMAIL_BODY(r, d, waNumber));
    window.location.href = `mailto:${r.email}?subject=${subject}&body=${body}`;
  };
  const sendSMS = (r) => {
    if (!r.mobile) { toast.error("No mobile number"); return; }
    const d = r.days_to_expiry ?? 0;
    window.location.href = `sms:${r.mobile}?body=${encodeURIComponent(REMIND_SMS(r, d, waNumber))}`;
  };

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">DSC Purchased — Customer Register</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track every Digital Signature Certificate sold, its validity, payment mode and renewal reminders (15-day pop-up alerts).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" onClick={printStatement} disabled={!can("dsc", "export")} data-testid="dsc-print-statement">
            <Printer size={16} className="mr-1.5" /> Print Statement
          </Button>
          <Button variant="outline" className="h-9" onClick={exportExcel} disabled={!can("dsc", "export")} data-testid="dsc-export">
            <MicrosoftExcelLogo size={16} className="mr-1.5" /> Excel
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button className="h-9" data-testid="dsc-new-btn" onClick={() => setEditing(null)}>
                <Plus size={15} weight="bold" className="mr-1.5" /> New DSC
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <ShieldCheck size={20} /> {editing ? "Edit DSC" : "Add DSC Record"}
                </DialogTitle>
              </DialogHeader>
              <DscForm
                customers={customers}
                products={products}
                services={services}
                editing={editing}
                certCompaniesPresets={certCompaniesPresets}
                certCompaniesUsed={certCompaniesUsed}
                onClose={() => { setOpen(false); setEditing(null); }}
                onSaved={load} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-flat p-4">
          <div className="eyebrow">Total (in view)</div>
          <div className="font-heading text-2xl font-semibold mt-0.5">{rows.length}</div>
        </div>
        <div className="card-flat p-4">
          <div className="eyebrow" style={{ color: "hsl(var(--success))" }}>Active</div>
          <div className="font-heading text-2xl font-semibold mt-0.5">{buckets.active}</div>
        </div>
        <div className="card-flat p-4">
          <div className="eyebrow" style={{ color: "hsl(var(--warning))" }}>Expiring &le; 15 days</div>
          <div className="font-heading text-2xl font-semibold mt-0.5">{buckets.expiring}</div>
        </div>
        <div className="card-flat p-4">
          <div className="eyebrow" style={{ color: "hsl(var(--destructive))" }}>Expired</div>
          <div className="font-heading text-2xl font-semibold mt-0.5">{buckets.expired}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="dsc-tabs">
          <TabsTrigger value="all" data-testid="dsc-tab-all">All</TabsTrigger>
          <TabsTrigger value="active" data-testid="dsc-tab-active">Active</TabsTrigger>
          <TabsTrigger value="expiring" data-testid="dsc-tab-expiring">Expiring ≤ 15 days</TabsTrigger>
          <TabsTrigger value="expired" data-testid="dsc-tab-expired">Expired</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="card-flat p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5 min-w-[240px] flex-1">
          <Label className="eyebrow">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Customer name, mobile, email, KYC ID, Order ID or description" data-testid="dsc-search" />
        </div>
        <div className="space-y-1.5 min-w-[180px]">
          <Label className="eyebrow">Certifying Authority</Label>
          <Select value={certCompany || "__all__"} onValueChange={(v) => setCertCompany(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="dsc-filter-cert"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All authorities</SelectItem>
              {[...(certCompaniesPresets.length ? certCompaniesPresets.filter((x) => x !== "Other") : CERT_COMPANIES.filter((x) => x !== "Other")),
                ...certCompaniesUsed,
                "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Purchase From</Label>
          <Input type="date" min="1900-01-01" value={start} onChange={(e) => setStart(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Purchase To</Label>
          <Input type="date" min="1900-01-01" value={end} onChange={(e) => setEnd(e.target.value)} className="font-mono" />
        </div>
        <Button variant="outline" className="h-9" onClick={() => { setQ(""); setCertCompany(""); setStart(""); setEnd(""); }}>Reset</Button>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Purchase Date</th>
              <th>Customer</th>
              <th>Mobile</th>
              <th>Authority</th>
              <th>KYC / Order ID</th>
              <th>Class</th>
              <th>Description</th>
              <th>Validity To</th>
              <th>Days Left</th>
              <th>Document (PDF)</th>
              <th className="num" style={{ textAlign: "right" }}>Reminders / Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-10 text-muted-foreground">No DSC records match the current filter.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`dsc-row-${r.id}`}>
                <td className="font-mono text-[12px]">{fmtDate(r.date)}</td>
                <td className="font-medium">
                  {r.customer_name}
                  {r.email && <div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{r.email}</div>}
                </td>
                <td className="font-mono text-[12px]">{r.mobile || "—"}</td>
                <td className="text-[12px]">
                  {r.certifying_company ? (
                    <span className="pill pill-info text-[10.5px]">{r.certifying_company}</span>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="font-mono text-[11.5px] leading-tight">
                  {r.kyc_id && <div>KYC: {r.kyc_id}</div>}
                  {r.order_id && <div>Ord: {r.order_id}</div>}
                  {!r.kyc_id && !r.order_id && <span className="text-muted-foreground">—</span>}
                </td>
                <td className="text-[12px]">{r.class_type}</td>
                <td className="text-[12px] max-w-[220px] truncate" title={r.description || ""}>
                  {r.description || <span className="text-muted-foreground">—</span>}
                </td>
                <td className="font-mono text-[12px]">{fmtDate(r.validity_to)}</td>
                <td className="font-mono text-[12px]">
                  {r._bucket === "expired" ? (
                    <span className="text-destructive font-semibold">Expired {Math.abs(r.days_to_expiry || 0)}d ago</span>
                  ) : r._bucket === "expiring" ? (
                    <span style={{ color: "hsl(var(--warning))" }} className="font-semibold">{r.days_to_expiry} day(s)</span>
                  ) : (
                    <span>{r.days_to_expiry} days</span>
                  )}
                </td>
                <td><DscDocumentCell row={r} onChanged={load} /></td>
                <td className="num" style={{ textAlign: "right" }}>
                  <div className="flex gap-1 justify-end flex-wrap">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                      title="Send WhatsApp reminder"
                      onClick={() => sendWhatsApp(r)} data-testid={`dsc-wa-${r.id}`}>
                      <WhatsappLogo size={13} weight="fill" style={{ color: "#25D366" }} />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                      title="Send email reminder"
                      onClick={() => sendEmail(r)} data-testid={`dsc-mail-${r.id}`}>
                      <EnvelopeSimple size={13} />
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                      title="Send SMS reminder"
                      onClick={() => sendSMS(r)} data-testid={`dsc-sms-${r.id}`}>
                      <ChatCircle size={13} />
                    </Button>
                    {r.temp_invoice_id ? (
                      <span className="pill pill-info text-[10px]" title={`Already sent as ${r.temp_invoice_number || 'Temp Invoice'}`}
                        data-testid={`dsc-temp-linked-${r.id}`}>
                        <Receipt size={11} className="inline mr-1" /> {r.temp_invoice_number || "Temp"}
                      </span>
                    ) : (
                      <Button size="sm" variant="outline"
                        className="h-7 px-2 text-[11px] border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                        title="Create a Temp Invoice from this DSC"
                        disabled={!r.customer_id || !(Number(r.amount) > 0)}
                        onClick={() => sendToTemp(r)}
                        data-testid={`dsc-send-temp-${r.id}`}>
                        <Receipt size={12} className="mr-1" /> Send to Temp Inv
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                      onClick={() => { setEditing(r); setOpen(true); }} data-testid={`dsc-edit-${r.id}`}>
                      <PencilSimple size={13} />
                    </Button>
                    {can("dsc", "delete") && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive"
                        onClick={() => del(r.id)}>
                        <Trash size={13} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
